import * as React from "react";
import { Formik, Field, Form, FormikHelpers, FieldProps } from "formik";
import { useTranslation } from "react-i18next";
import { FormControl, FormErrorMessage } from "@chakra-ui/form-control";
import {
  Stack,
  ModalFooter,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Button,
  Avatar,
  useDisclosure,
  Divider,
} from "@chakra-ui/react";
import ReactStars from "react-stars";
import * as Yup from "yup";
import { Input } from "@chakra-ui/input";
import { Label } from "../../components/Label";
import { Evaluation, EvaluationObjective } from "../../types";
import { useCallback, useMemo, useState } from "react";
import { DatePicker } from "../DatePicker";
import { useStores } from "../../hooks/useStores";
import { PrimayButton } from "../Button";
import { FileUploadModal } from "../FileUploadModal";

interface Values {
  id: string;
  weight: string;
  lean_mass: string;
  breast: string;
  waist: string;
  thigh: string;
  data: string;
  photo_url: string;
  objectives: EvaluationObjective[];
}

type EvaluationFormProps = {
  onSubmit: (data: any) => any;
  evaluation?: Evaluation | null;
  onCancel: () => any;
};

export const EvaluationForm: React.FC<EvaluationFormProps> = ({
  onSubmit,
  evaluation,
  onCancel,
}) => {
  const { clientsStore } = useStores();
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [tabIndex, setTabIndex] = useState(0);

  const handleSliderChange = (event) => {
    setTabIndex(parseInt(event.target.value, 10));
  };

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  const handleSubmit = useCallback(
    (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
      onSubmit(values);
    },
    [onSubmit]
  );

  const initValues = useMemo(() => {
    var objectivesArray: any[] = [];

    if (
      evaluation &&
      evaluation.objectives &&
      evaluation.objectives.length > 0
    ) {
      objectivesArray = evaluation.objectives.map((genericObjective) => {
        return {
          objective: genericObjective.objective,
          valutation: genericObjective.valutation,
          comment: genericObjective.comment,
        };
      });
    } else {
      objectivesArray = clientsStore.genericObjectives.map(
        (genericObjective) => {
          return {
            objective: genericObjective,
            valutation: 0,
            comment: "",
          };
        }
      );
    }

    return {
      id: evaluation?.id ? evaluation?.id : "",
      weight: evaluation?.weight ? evaluation?.weight : "",
      lean_mass: evaluation?.lean_mass ? evaluation?.lean_mass : "",
      data: evaluation?.data ? evaluation?.data : "",
      breast: evaluation?.breast ? evaluation?.breast : "",
      waist: evaluation?.waist ? evaluation?.waist : "",
      thigh: evaluation?.thigh ? evaluation?.thigh : "",
      photo_url: evaluation?.photo_url || "",
      objectives: objectivesArray,
    };
  }, [evaluation, clientsStore.genericObjectives]);

  return (
    <Formik
      initialValues={initValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        data: Yup.string().required(
          t("screens.client.evaluationFormValidations.dataRequired")
        ),
        weight: Yup.string().required(
          t("screens.client.evaluationFormValidations.weightRequired")
        ),
        objectives: Yup.array().of(
          Yup.object().shape({
            valutation: Yup.number()
              .min(
                1,
                t("screens.client.evaluationFormValidations.valutationMin")
              )
              .required(
                t("screens.client.evaluationFormValidations.valuationRequired")
              ),
            comment: Yup.string().required(
              t("screens.client.evaluationFormValidations.commentRequired")
            ),
          })
        ),
      })}
    >
      {(props) => (
        <Form
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Field type={"hidden"} name={"id"} />
          <Tabs
            colorScheme="green"
            size="md"
            isFitted
            isLazy
            variant="enclosed"
            index={tabIndex}
            onChange={handleTabsChange}
          >
            <TabList>
              <Tab _selected={{ color: "white", bg: "#562D5F" }}>
                {t("screens.client.evaluationForm.results")}
              </Tab>
              <Tab _selected={{ color: "white", bg: "#562D5F" }}>
                {t("screens.client.evaluationForm.objectives")}
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Stack
                  spacing={"1rem"}
                  direction={["column", "row"]}
                  mb={"20px"}
                >
                  <Field id={"weight"} name={"weight"}>
                    {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                      <FormControl
                        isInvalid={Boolean(meta.error && meta.touched)}
                      >
                        <Label fontWeight="900">
                          {t("screens.client.evaluationForm.weight")}
                        </Label>
                        <Input
                          size="md"
                          type={"text"}
                          name={field.name}
                          value={field.value}
                          onChange={(event) =>
                            setFieldValue(field.name, event.target.value)
                          }
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field id={"lean_mass"} name={"lean_mass"}>
                    {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                      <FormControl
                        isInvalid={Boolean(meta.error && meta.touched)}
                      >
                        <Label>
                          {t("screens.client.evaluationForm.lean_mass")}
                        </Label>
                        <Input
                          size="md"
                          type={"text"}
                          name={field.name}
                          value={field.value}
                          onChange={(event) =>
                            setFieldValue(field.name, event.target.value)
                          }
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>
                <Stack
                  spacing={"1rem"}
                  direction={["column", "row"]}
                  mb={"20px"}
                >
                  <Field id={"breast"} name={"breast"}>
                    {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                      <FormControl
                        isInvalid={Boolean(meta.error && meta.touched)}
                      >
                        <Label>
                          {t("screens.client.evaluationForm.breast")}
                        </Label>
                        <Input
                          size="md"
                          type={"text"}
                          name={field.name}
                          value={field.value}
                          onChange={(event) =>
                            setFieldValue(field.name, event.target.value)
                          }
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field id={"waist"} name={"waist"}>
                    {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                      <FormControl
                        isInvalid={Boolean(meta.error && meta.touched)}
                      >
                        <Label>
                          {t("screens.client.evaluationForm.waist")}
                        </Label>
                        <Input
                          size="md"
                          type={"text"}
                          name={field.name}
                          value={field.value}
                          onChange={(event) =>
                            setFieldValue(field.name, event.target.value)
                          }
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>
                <Stack
                  spacing={"1rem"}
                  direction={["column", "row"]}
                  mb={"20px"}
                >
                  <Field id={"thigh"} name={"thigh"}>
                    {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                      <FormControl
                        isInvalid={Boolean(meta.error && meta.touched)}
                      >
                        <Label>
                          {t("screens.client.evaluationForm.thigh")}
                        </Label>
                        <Input
                          size="md"
                          type={"text"}
                          name={field.name}
                          value={field.value}
                          onChange={(event) =>
                            setFieldValue(field.name, event.target.value)
                          }
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field id={"data"} name={"data"}>
                    {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                      <FormControl
                        isInvalid={Boolean(meta.error && meta.touched)}
                      >
                        <Label fontWeight="900">
                          {t("screens.client.evaluationForm.data")}
                        </Label>
                        <DatePicker
                          // @ts-ignore
                          size="md"
                          dateFormat={"dd/MM/yyyy"}
                          selectedDate={
                            props.values.data
                              ? new Date(props.values.data)
                              : undefined
                          }
                          onChange={(date) => {
                            // let date = new Date(item);
                            // if(date){
                            // let formatData =
                            //   date?.getDay() +
                            //   "/" +
                            //   date?.getMonth() +
                            //   "/" +
                            //   date?.getFullYear();

                            setFieldValue("data", date);
                            // setFieldValue("data", formatData);
                          }}
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>
                <Stack>
                  <Field id={"photo_url"} name={"photo_url"}>
                    {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                      <FormControl
                        isInvalid={Boolean(meta.error && meta.touched)}
                      >
                        <Label>
                          {t("screens.client.evaluationForm.photo")}
                        </Label>

                        <Avatar size="2xl" src={field.value} />
                        <Button
                          variant={"ghost"}
                          onClick={onOpen}
                          marginTop={"0.5rem"}
                        >
                          {t("screens.client.evaluationForm.uploadPhoto")}
                        </Button>
                        <FileUploadModal
                          isOpen={isOpen}
                          onClose={onClose}
                          onFileUploaded={(imageUrls: string[]) => {
                            setFieldValue("photo_url", imageUrls[0]);
                            onClose();
                          }}
                          accept={"image/jpeg, image/png"}
                        />

                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>
              </TabPanel>
              <TabPanel>
                {props.values.objectives.map((objective, index) => (
                  <>
                    <Stack
                      spacing={"1rem"}
                      direction={["column", "row"]}
                      mb={"20px"}
                    >
                      <Field>
                        {({
                          field,
                          form: { setFieldValue },
                          meta,
                        }: FieldProps) => (
                          <FormControl>
                            <Label>
                              {t("screens.client.evaluationForm.title")}
                            </Label>
                            <Input
                              size="md"
                              type={"text"}
                              value={objective.objective.title}
                              disabled
                            />
                          </FormControl>
                        )}
                      </Field>
                    </Stack>
                    <Stack
                      spacing={"1rem"}
                      direction={["column", "row"]}
                      mb={"20px"}
                    >
                      <Field>
                        {({
                          field,
                          form: { setFieldValue },
                          meta,
                        }: FieldProps) => (
                          <FormControl>
                            <Label>
                              {t("screens.client.evaluationForm.description")}
                            </Label>
                            <Input
                              size="md"
                              type={"text"}
                              value={objective.objective.description}
                              disabled
                            />
                          </FormControl>
                        )}
                      </Field>
                    </Stack>
                    <Stack
                      spacing={"1rem"}
                      direction={["column", "row"]}
                      mb={"20px"}
                    >
                      <Field name={"objectives." + index + ".valutation"}>
                        {({
                          field,
                          form: { setFieldValue },
                          meta,
                        }: FieldProps) => (
                          <FormControl
                            isInvalid={Boolean(meta.error && meta.touched)}
                          >
                            <Label>
                              {t("screens.client.evaluationForm.valutation")}
                            </Label>
                            <ReactStars
                              count={5}
                              half={false}
                              value={field.value}
                              onChange={(event) =>
                                setFieldValue(field.name, event)
                              }
                              size={24}
                              color2={"#ffd700"}
                            />
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Stack>
                    <Stack
                      spacing={"1rem"}
                      direction={["column", "row"]}
                      mb={"20px"}
                    >
                      <Field name={"objectives." + index + ".comment"}>
                        {({
                          field,
                          form: { setFieldValue },
                          meta,
                        }: FieldProps) => (
                          <FormControl
                            isInvalid={Boolean(meta.error && meta.touched)}
                          >
                            <Label>
                              {t("screens.client.evaluationForm.comment")}
                            </Label>
                            <Textarea
                              size="md"
                              type={"text"}
                              name={field.name}
                              value={field.value}
                              onChange={(event) =>
                                setFieldValue(field.name, event.target.value)
                              }
                            />
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Stack>

                    <Divider size={"lg"} mb="15px" mt="15px" />
                  </>
                ))}
              </TabPanel>
            </TabPanels>
          </Tabs>
          <ModalFooter>
            <Button
              type={"reset"}
              variant={"ghost"}
              onClick={onCancel}
              mx="5px"
            >
              {t("common.cancel")}
            </Button>

            {tabIndex === 0 ? (
              <PrimayButton
                type={"button"}
                onClick={() => handleTabsChange(1)}
                disabled={
                  props.values.data.length === 0 ||
                  props.values.weight.length === 0
                }
                mx="5px"
              >
                {t("common.continue")}
              </PrimayButton>
            ) : (
              <>
                <Button
                  type={"button"}
                  onClick={() => handleTabsChange(0)}
                  variant="ghost"
                  mx="5px"
                >
                  {t("common.back")}
                </Button>
                <PrimayButton type={"submit"} isLoading={false} mx="5px">
                  {t("common.save")}
                </PrimayButton>
              </>
            )}
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
};
